import { useCallback, useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import { focusOutline } from '@hivebrite/design-system/Theme';

import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

import { AttachedImageFitting } from './AttachedImageFitting';
import type { AttachedImageProps } from './interfaces';
import useSessionStorageAttachedImages from './useSessionStorageAttachedImages';

/**
  `@hivebrite/design-system/Image`, uses the browser-native `loading="lazy"` which
  only loads images when they are visible.
 */

export const AttachedImage = ({
  breakpoint,
  canDisplay,
  displayMoreImages,
  height,
  image,
  isSingle,
  marginX,
  maxHeight,
  moreImagesCount,
  onClick,
}: AttachedImageProps) => {
  const [fittedHeights, storeFittedHeights] = useSessionStorageAttachedImages();

  const [isLoaded, setIsLoaded] = useState(false);

  const imgRef = useRef<HTMLImageElement>(null);

  const handleClick = useCallback(() => onClick(image.id), [image, onClick]);

  useEffect(() => {
    if (imgRef?.current && (imgRef?.current as HTMLImageElement).complete) {
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
    }
  }, [canDisplay, imgRef]);

  return isSingle && fittedHeights[image.id]?.[breakpoint] === undefined ? (
    <Box flexDirection="column" width="100%" position="relative">
      <LoadingWrapper>
        <Skeleton height="100%" width="100%" position="absolute" />
      </LoadingWrapper>
      <AttachedImageFitting
        breakpoint={breakpoint}
        fittedHeights={fittedHeights}
        height={height}
        image={image}
        isSingle={isSingle}
        maxHeight={maxHeight}
        storeFittedHeights={storeFittedHeights}
      />
    </Box>
  ) : (
    <ImageWrapper
      tabIndex={0}
      role="button"
      alignItems="center"
      onClick={handleClick}
      onKeyPress={onEnterOrSpacePress(handleClick)}
    >
      <ContainerImage
        height={isSingle ? `${fittedHeights[image.id][breakpoint]}px` : height}
      >
        <LoadingWrapper
          display={isLoaded ? 'none' : 'block'}
          height={
            isSingle ? `${fittedHeights[image.id][breakpoint]}px` : height
          }
        >
          <Skeleton height="100%" width="100%" />
        </LoadingWrapper>
        <Image
          alt=""
          css={
            isSingle
              ? css`
                  margin-left: ${marginX};
                  margin-right: ${marginX};
                  max-height: ${fittedHeights[image.id][breakpoint]}px;
                  max-width: 100%;
                  object-fit: contain;
                `
              : css`
                  object-fit: cover;
                  width: 100%;
                `
          }
          data-testid={`attached-image-id:${image.id}`}
          height={
            isSingle ? `${fittedHeights[image.id][breakpoint]}px` : height
          }
          onLoad={() => {
            setIsLoaded(true);
          }}
          ref={imgRef}
          // Fallback in case if image is returned in old LF image format
          src={image.large_url ?? image.default_url}
        />
      </ContainerImage>
      {displayMoreImages && (
        <MoreImageOverlay>
          <Box alignItems="center">
            <Box color="white" fontWeight={2} fontSize={9} mr={1}>
              +
            </Box>
            <Box color="white" fontWeight={2} fontSize={10}>
              {moreImagesCount}
            </Box>
            <ScreenReadersContent>
              {s__('LiveFeedAccessibility|extra images')}
            </ScreenReadersContent>
          </Box>
        </MoreImageOverlay>
      )}
      <ScreenReadersContent>
        {s__('LiveFeedAccessibility|See image in carousel')}
      </ScreenReadersContent>
    </ImageWrapper>
  );
};

const ContainerImage = styled(Box)`
  display: block;
  width: 100%;
  position: relative;
  text-align: center;
`;

const LoadingWrapper = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const ImageWrapper = styled(Box)`
  ${focusOutline}
  cursor: pointer;
  position: relative;
  width: 100%;
`;

const MoreImageOverlay = styled(Box).attrs(() => ({
  bg: 'fog',
}))`
  align-items: center;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
